'use client';

import { ResourceNavigation } from '@ui/components/moleculas';
import { IConversationSet } from '@ui/components/organisms/conversation';
import { useMessageContext } from '@ui/components/organisms/message';
import { useMemo, useCallback, useState, useEffect } from 'react';
import { ContextUserType } from '@ui/constants/context';
import { MessageUpdateFormPortal } from '../common/message-update-form-portal';
import { MessageUpdateFormProvider } from '@ui/contexts/message-update-form-context';
import { PlusIcon, Info } from 'lucide-react';
import { Button } from '@ui/components/ui/button';
import { useRouter, useSearchParams } from 'next/navigation';
import { usePostHogFeatureFlag, useCommunityTabs } from '@ui/hooks';
import PostListSegment from './post-list-segment';
import { TopicListItem } from '../categories/topic-list-item';
import { DynamicRouteBuilder } from '@ui/constants';
import { twMerge } from 'tailwind-merge';
import { BasePostCreationForm } from '@ui/components/organisms/community/components/base-post-creation-form';
import { IMessagePayload, useToast } from '@ui/components';
import { sendGAEvent } from '@next/third-parties/google';
import { toast as sonnerToast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@ui/components/ui/sheet';
import { CommunitySidebar } from '@ui/components/organisms/community/components/community-sidebar';
import { useInteractionGuard } from '@hooks/index';

const filterOptions = ['Published', 'My Drafts'] as const;
const sortOptions = ['Newest', 'Trending', 'Most Engaged'] as const;

type FilterOption = (typeof filterOptions)[number];
type SortOption = (typeof sortOptions)[number];
type FilterOrSort = FilterOption | SortOption;

interface IProps {
  emptyState: {
    title: string;
    message: string;
  };
  userType: ContextUserType;
  isLoadingCommunityConversations: boolean;
  isCommunityConversationSuccess: boolean;
  communitySets: IConversationSet[];
  topics?: IConversationSet[];
  isDynamicRoute?: boolean;
  isAuthenticated?: boolean;
  isCommunityView?: boolean;
  conversationState?: any;
  onSearchIconClick?: () => void;
  viewType?: 'community' | 'topic';
  communityOwnerSlug: string;
  defaultCommunitySlug: string;
  showBreadcrumbs: boolean;
  showResourceNavigationTitle: boolean;
  hasCreatorEnabledTopics: boolean;
  hasCreatorEnabledFansCanCreatePosts: boolean;
  isTopicSelected: boolean;
  communitySet: IConversationSet;
  defaultCommunitySet: IConversationSet;
  creatorData?: { platform_settings?: { enable_video_uploads?: boolean } };
  reportsDrawer?: React.ReactNode;
  onViewReports: () => void;
}
export const PostList = ({
  emptyState,
  userType,
  isLoadingCommunityConversations,
  isCommunityConversationSuccess,
  communitySets,
  isDynamicRoute,
  isAuthenticated,
  topics,
  conversationState,
  isCommunityView = true,
  viewType,
  communityOwnerSlug,
  defaultCommunitySlug,
  showBreadcrumbs,
  showResourceNavigationTitle,
  hasCreatorEnabledTopics,
  hasCreatorEnabledFansCanCreatePosts,
  isTopicSelected,
  communitySet,
  defaultCommunitySet,
  creatorData,
  reportsDrawer,
  onSearchIconClick,
  onViewReports,
}: IProps) => {
  const messageState = useMessageContext(userType);
  const [selectedFilter, setSelectedFilter] = useState<FilterOrSort>('Published');
  const [selectedSort, setSelectedSort] = useState<SortOption>('Newest');
  const isFanEnabledToCreatePost = usePostHogFeatureFlag('enable-fan-to-create-post');
  const enableTopicsFeature = usePostHogFeatureFlag('enable-creator-to-create-topic');
  const isPostFilterEnabled = usePostHogFeatureFlag('enable-post-filter');
  const enableSearchFeature = usePostHogFeatureFlag('enable-search-feature');
  const isModerationEnabled = usePostHogFeatureFlag('community-moderation');
  const router = useRouter();
  const [initialLoading, setInitialLoading] = useState(true);
  const [showCreatePostForm, setShowCreatePostForm] = useState(false);
  const [showCommunitySidebar, setShowCommunitySidebar] = useState(false);
  const { interceptInteraction } = useInteractionGuard();

  // Get tab from URL using SSR-safe hook
  const searchParams = useSearchParams();
  const tabFromQuery = searchParams?.get('tab');

  const isLoading = useMemo(
    () =>
      isLoadingCommunityConversations ||
      messageState.isLoading ||
      messageState.isMessagesUninitialized,
    [isLoadingCommunityConversations, messageState.isLoading, messageState.isMessagesUninitialized],
  );

  const showEmptyScreen = useMemo(() => {
    const hasCommunity = isCommunityConversationSuccess && communitySets.length > 0;
    const hasNoMessages = messageState.isSuccess && messageState.messages.length === 0;
    const hasNoPinnedMessages = !messageState.pinned_messages?.length;
    const isNotLoading = !isLoading && !messageState.isFetching && !initialLoading;

    return hasCommunity && hasNoMessages && hasNoPinnedMessages && isNotLoading;
  }, [
    isCommunityConversationSuccess,
    communitySets.length,
    messageState.isSuccess,
    messageState.messages.length,
    messageState.pinned_messages?.length,
    messageState.isFetching,
    isLoading,
    initialLoading,
  ]);

  const joinMessage = useMemo(() => {
    const creatorDisplayName =
      (messageState as any)?.community_details?.creator_display_name ||
      (communitySet as any)?.conversation?.creator_display_name;

    return creatorDisplayName
      ? `Join community to use this feature. Collect this badge to join community!`
      : 'Join this KOR Space to use this feature. Collect this badge to join.';
  }, [messageState, communitySet]);

  const draftEmptyState = useMemo(
    () => ({
      title: 'No Draft Posts',
      message:
        "You haven't created any draft posts yet. Create a new post and save it as a draft to see it here.",
    }),
    [],
  );

  const handleCreatePost = useCallback((event?: React.MouseEvent) => {
    setShowCreatePostForm((prev) => !prev);
  }, []);

  const handleCreatePostWithGuard = useMemo(
    () =>
      interceptInteraction((event?: React.MouseEvent) => {
        handleCreatePost(event);
      }, joinMessage),
    [interceptInteraction, handleCreatePost, joinMessage],
  );

  const toast = useToast();

  const handleCreatePostSubmit = useCallback(
    async (
      message: IMessagePayload,
      description: string,
      mentions: string[],
      clearEditorContent: () => void,
      clearMessage: () => void,
      discardFiles: () => void,
    ) => {
      try {
        const res = await messageState.onCreateMessage({
          message: {
            ...message,
            description,
            mentions,
            creator_id: communitySet?.conversation?.creator_id,
            thread_id: communitySet?.conversation?.main_thread,
            user_id: messageState.user_id,
            created_by_creator: false,
          },
        });
        if (res?.data?.message?.id) {
          clearMessage();
          discardFiles();
          clearEditorContent();
          // Send GA Event for Successful Post
          sendGAEvent('event', 'CreateFanPostSuccessful', {
            event_label: 'Fan Post creation success',
            fanId: messageState.user_id,
            postId: res?.data.message?.id,
          });
          // create post thread
          await messageState.onCreateMessageThread({
            message_id: res?.data?.message?.id,
            conversation_id: res?.data?.message_thread?.conversation_id,
            queryParams: messageState.query,
          });

          sonnerToast('You created a new post', {
            description: new Date().toLocaleString(),
          });

          setShowCreatePostForm(false);
        } else {
          toast({ title: 'Error creating post, please try again' }, { type: 'error' });
        }
      } catch (error) {
        console.error(error);
        toast({ title: 'Error creating post, please try again' }, { type: 'error' });

        // Send GA Event for failed Post
        sendGAEvent('event', 'CreateFanPostFailed', {
          fanId: messageState.user_id,
          event_label: 'Fan Post creation Failed',
        });
      }
    },
    [messageState, communitySet, toast],
  );

  const handleCancelCreatePost = useCallback(() => {
    setShowCreatePostForm(false);
  }, []);

  const getEmptyState = useCallback(() => {
    if (selectedFilter === 'My Drafts') {
      return draftEmptyState;
    }

    // Only show topic-specific empty state for fans
    if (viewType === 'topic' && userType === 'fan') {
      const topicName = communitySet?.conversation?.name || 'this topic';
      return {
        title: `No posts yet in ${topicName}`,
        message: `Be the first to share something in this topic. Your post will inspire others to join in!`,
        actionButton: {
          label: 'Create Post',
          onClick: handleCreatePost,
        },
      };
    }

    return emptyState;
  }, [
    selectedFilter,
    emptyState,
    viewType,
    userType,
    handleCreatePost,
    draftEmptyState,
    communitySet?.conversation?.name,
  ]);

  const hasCreatorEnabledFansToCreatePost = useMemo(() => {
    return defaultCommunitySet?.restrictions_list?.fans_can_create_posts;
  }, [defaultCommunitySet]);

  const breadcrumbs = useMemo(() => {
    const conversation = communitySet?.conversation;
    if (conversation?.conversation_type === 'topic') {
      return [
        {
          title: `/ ${conversation.name}`,
        },
      ];
    }
    return [];
  }, [communitySet?.conversation]);

  useEffect(() => {
    if (isCommunityConversationSuccess && messageState.isSuccess) {
      setInitialLoading(false);
    }
  }, [isCommunityConversationSuccess, messageState.isSuccess]);

  useEffect(() => {
    if (isPostFilterEnabled) {
      setSelectedSort('Newest');
    } else {
      setSelectedFilter('Published');
    }
  }, [isPostFilterEnabled]);

  const nextPage = useCallback(() => {
    messageState.onNextPage();
  }, [messageState]);

  const {
    tabs,
    selectedTab,
    resourceTabs: baseResourceTabs,
    onTabChanged,
  } = useCommunityTabs({
    creatorDisplayName: messageState.community_details?.creator_display_name,
    conversationType: communitySet?.conversation?.conversation_type,
    hasCreatorEnabledFansCanCreatePosts,
    hasCreatorEnabledTopics,
    initialSelectedTab: tabFromQuery ? parseInt(tabFromQuery) : undefined,
    isTopicSelected,
    defaultCommunitySet,
    onTabChange: (tab) => {
      const tabIndex = tabs.indexOf(tab);
      if (tabIndex === -1) return;

      messageState.setSetQuery({
        ...messageState.query,
        page: 1,
        draft: false,
        ordering: 'desc',
        conversation_type: communitySet?.conversation?.conversation_type,
        ...(isCommunityView && { created_by_creator: tabIndex === 1 ? false : true }),
      });

      if (tabIndex === 1) {
        setSelectedFilter('Published');
        setSelectedSort('Newest');
      } else {
        if (isPostFilterEnabled) {
          setSelectedSort('Newest');
        } else {
          setSelectedFilter('Published');
        }
      }

      if (userType === 'fan') {
        router.push(
          `${DynamicRouteBuilder.communityBySlug(
            communityOwnerSlug,
            defaultCommunitySlug,
          )}?tab=${tabIndex}`,
        );
      } else {
        router.push(`/dashboard/community?tab=${tabIndex}`);
      }
    },
  });

  const getFilterOptions = useMemo(() => {
    if (userType === 'creator' && selectedTab === 0) {
      return [...filterOptions] as FilterOption[];
    }
    return [] as FilterOption[];
  }, [userType, selectedTab]);

  const getSortOptions = useMemo(() => {
    const topicsTabIndex = tabs.findIndex(
      (tab) => tab === defaultCommunitySet?.conversation?.miscellaneous?.topics_tab_label,
    );

    if (!isTopicSelected && selectedTab === 1) {
      return [] as SortOption[];
    }

    if (isPostFilterEnabled) {
      if (selectedTab === 1) {
        return sortOptions.filter((option) => option !== 'Most Engaged') as SortOption[];
      }
      return [] as SortOption[];
    }
    return [] as SortOption[];
  }, [
    isPostFilterEnabled,
    selectedTab,
    tabs,
    defaultCommunitySet?.conversation?.miscellaneous?.topics_tab_label,
    isTopicSelected,
  ]);

  const handleFilterClick = useCallback(
    (filter: FilterOrSort) => {
      let ordering: 'desc' | 'popular' | 'pondered' | 'time_decaying' | 'time_decaying_votes' =
        'desc';
      let draft = false;

      if (filter === 'My Drafts') {
        setSelectedFilter('My Drafts');
        draft = true;
      } else if (filter === 'Published') {
        setSelectedFilter('Published');
        draft = false;
      } else if (filter === 'Newest') {
        setSelectedSort('Newest');
        ordering = 'desc';
      } else if (filter === 'Trending') {
        setSelectedSort('Trending');
        ordering = 'time_decaying';
      } else if (filter === 'Most Engaged') {
        setSelectedSort('Most Engaged');
        ordering = 'time_decaying_votes';
      }

      if (sortOptions.includes(filter as SortOption)) {
        if (selectedTab === 1) {
          draft = false;
        } else {
          draft = selectedFilter === 'My Drafts';
        }
      }

      messageState.setSetQuery({
        ...messageState.query,
        page: 1,
        draft,
        ordering,
      });
    },
    [messageState, selectedFilter, selectedTab],
  );

  const resourceTabs = useMemo(() => {
    if (tabs.length === 0) {
      return [];
    }

    return baseResourceTabs.map((tab, index) => ({
      ...tab,
      content: (
        <div>
          {/* Filter and Sort Options */}
          {(isPostFilterEnabled || getFilterOptions.length > 0) &&
            (getFilterOptions.length > 0 || getSortOptions.length > 0) && (
              <div className="flex gap-2 p-2">
                <div className="flex gap-2">
                  {getFilterOptions.map((filter) => (
                    <Button
                      key={filter}
                      variant="outline"
                      size="sm"
                      className={twMerge(
                        'outline-none border-none shadow-none',
                        selectedFilter === filter && 'bg-gray-200',
                      )}
                      onClick={() => handleFilterClick(filter)}>
                      {filter}
                    </Button>
                  ))}
                  {userType === 'creator' &&
                    getFilterOptions.length > 0 &&
                    getSortOptions.length > 0 && <div className="w-px bg-gray-300 mx-1" />}
                  {getSortOptions.map((sort) => (
                    <Button
                      key={sort}
                      variant="outline"
                      size="sm"
                      className={twMerge(
                        'outline-none border-none shadow-none',
                        selectedSort === sort && 'bg-gray-200',
                      )}
                      onClick={() => handleFilterClick(sort)}>
                      {sort}
                    </Button>
                  ))}
                </div>
              </div>
            )}

          {index === 0 && (
            <PostListSegment
              messageState={messageState}
              isLoading={isLoading}
              initialLoading={initialLoading}
              emptyState={getEmptyState()}
              showEmptyScreen={showEmptyScreen}
              nextPage={nextPage}
              userType={userType}
              isDynamicRoute={isDynamicRoute}
              moderators_ids={defaultCommunitySet?.restrictions_list?.moderators_ids || []}
            />
          )}

          {index === 1 && (
            <>
              {!isTopicSelected && hasCreatorEnabledTopics && enableTopicsFeature ? (
                <TopicListItem
                  topics={topics ?? []}
                  conversationState={conversationState}
                  userType={userType}
                />
              ) : (
                <PostListSegment
                  messageState={messageState}
                  isLoading={isLoading}
                  initialLoading={initialLoading}
                  emptyState={getEmptyState()}
                  showEmptyScreen={showEmptyScreen}
                  nextPage={nextPage}
                  userType={userType}
                  isDynamicRoute={isDynamicRoute}
                  moderators_ids={defaultCommunitySet?.restrictions_list?.moderators_ids || []}
                />
              )}
            </>
          )}
        </div>
      ),
    }));
  }, [
    baseResourceTabs,
    tabs,
    messageState,
    isLoading,
    initialLoading,
    getEmptyState,
    showEmptyScreen,
    nextPage,
    userType,
    isDynamicRoute,
    hasCreatorEnabledTopics,
    enableTopicsFeature,
    topics,
    conversationState,
    isPostFilterEnabled,
    getFilterOptions,
    getSortOptions,
    selectedFilter,
    selectedSort,
    handleFilterClick,
    isTopicSelected,
  ]);

  const renderResourceNavigation = useMemo(() => {
    if (tabs.length === 0 || selectedTab === undefined) {
      return null;
    }

    const createPostFormContent = showCreatePostForm ? (
      <div className="bg-white mt-4 mb-8">
        <BasePostCreationForm
          onSubmit={handleCreatePostSubmit}
          authorAvatarURL={messageState.user_avatar_url}
          userType="fan"
          currentUser="fan"
          isLoading={messageState.isCreating || messageState.isPostingImage}
          placeholder="Compose a post..."
          editorLabel="Share a post"
          buttonLabel="Post"
          enableSuggestions
          authorAvatarFallbackName={messageState.user_username}
          onCancel={handleCancelCreatePost}
          showCancelButton={true}
          creatorData={creatorData}
        />
      </div>
    ) : null;

    const creatorId =
      communityOwnerSlug ||
      messageState.community_details?.creator_id ||
      messageState.community_details?.creator_display_name ||
      'unknown';
    const navigationKey = `community-nav-${creatorId}-${
      communitySet?.conversation?.conversation_type || 'community'
    }`;

    return (
      <ResourceNavigation
        key={navigationKey}
        showResourceNavigationTitle={showResourceNavigationTitle}
        tabs={resourceTabs}
        breadcrumbs={breadcrumbs}
        activeTab={tabs[selectedTab] || tabs[0]}
        selectedTabIndex={selectedTab}
        onTabChange={onTabChanged}
        showBreadcrumbs={showBreadcrumbs}
        showSearch={enableSearchFeature || false}
        searchPlaceholder="Search posts, threads..."
        onSearchClick={onSearchIconClick}
        showAdditionalContent={showCreatePostForm}
        additionalContent={createPostFormContent}
        actionButtons={
          <>
            {selectedTab === 1 &&
              userType === 'fan' &&
              messageState.isFanOfCreator &&
              isFanEnabledToCreatePost &&
              isAuthenticated &&
              hasCreatorEnabledFansToCreatePost &&
              !hasCreatorEnabledTopics && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCreatePostWithGuard}
                  className="whitespace-nowrap bg-white border border-slate-200 hover:bg-gray-50">
                  <PlusIcon className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Create Post</span>
                </Button>
              )}

            {viewType === 'topic' && isAuthenticated && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleCreatePostWithGuard}
                className="whitespace-nowrap bg-white border border-slate-200 hover:bg-gray-50">
                <PlusIcon className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Create Post</span>
              </Button>
            )}

            {isModerationEnabled && communitySets.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCommunitySidebar(true)}
                className="p-2 h-8 w-8 lg:hidden bg-white border border-slate-200 hover:bg-gray-50">
                <Info className="h-4 w-4" />
              </Button>
            )}
          </>
        }
      />
    );
  }, [
    tabs,
    selectedTab,
    breadcrumbs,
    resourceTabs,
    showResourceNavigationTitle,
    enableSearchFeature,
    userType,
    messageState.isFanOfCreator,
    isFanEnabledToCreatePost,
    isAuthenticated,
    hasCreatorEnabledFansToCreatePost,
    hasCreatorEnabledTopics,
    viewType,
    communitySets,
    isModerationEnabled,
    showCreatePostForm,
    handleCreatePostSubmit,
    handleCancelCreatePost,
    messageState.user_avatar_url,
    messageState.isCreating,
    messageState.isPostingImage,
    messageState.user_username,
    handleCreatePost,
    onSearchIconClick,
    onTabChanged,
    showBreadcrumbs,
    communityOwnerSlug,
    communitySet?.conversation?.conversation_type,
  ]);

  return (
    <MessageUpdateFormProvider>
      <>
        {messageState.messages.length > 0 && (
          <MessageUpdateFormPortal userType={userType} main_post_ref="" />
        )}
        <div>{renderResourceNavigation}</div>

        <Sheet open={showCommunitySidebar} onOpenChange={setShowCommunitySidebar}>
          <SheetContent side="bottom" closeButtonClassName={''} className="z-[9999999]">
            <SheetHeader>
              <SheetTitle>Community Info</SheetTitle>
            </SheetHeader>
            <div className="mt-6">
              <CommunitySidebar
                userType={userType}
                communitySet={communitySet}
                masterCommunitySet={defaultCommunitySet}
                isCommunitySuccess={isCommunityConversationSuccess}
                isLoadingCommunity={isLoadingCommunityConversations}
                userId={messageState.user_id}
                onViewReports={() => {
                  setShowCommunitySidebar(false);
                  onViewReports();
                }}
              />
            </div>
          </SheetContent>
        </Sheet>
        {reportsDrawer && reportsDrawer}
      </>
    </MessageUpdateFormProvider>
  );
};
